import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { EntraAuthProvider, useEntraAuth, EntraConfigChecker } from './components/EntraAuth';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import LoginEntra from './LoginEntra';
import Patients from './pages/Patients';
import Appointments from './pages/Appointments';
import Doctors from './pages/Doctors';
import Staff from './pages/Staff';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Sisteme from './pages/Sisteme';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useEntraAuth();

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Se încarcă...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/" replace />;
};

// Intranet Component
const Intranet = () => {
  const { user } = useEntraAuth();

  const handleLogout = () => {
    // Clear all storage including overlay state
    sessionStorage.removeItem('redirectOverlay');
    localStorage.clear();
    sessionStorage.clear();

    // Clear MSAL cache
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('msal')) {
        localStorage.removeItem(key);
      }
    });

    // Redirect to evoomedical.ro with logout flag to prevent overlay
    window.location.href = 'https://www.evoomedical.ro?logout=true';
  };

  return (
    <div className="dashboard-layout">
      {/* Sidebar Navigation */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Evoo Medical</h1>
          <p>Portal Compas</p>
        </div>
        <nav className="sidebar-nav">
          <NavLink to="/dashboard" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <span className="icon">📊</span>
            Dashboard
          </NavLink>
          <NavLink to="/sisteme" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <span className="icon">🔧</span>
            Sistem și Aplicații
          </NavLink>
          <NavLink to="/patients" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <span className="icon">👥</span>
            Pacienți
          </NavLink>
          <NavLink to="/appointments" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <span className="icon">📅</span>
            Programări
          </NavLink>
          <NavLink to="/doctors" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <span className="icon">👨‍⚕️</span>
            Doctori
          </NavLink>
          <NavLink to="/staff" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <span className="icon">👔</span>
            Personal
          </NavLink>
          <NavLink to="/reports" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <span className="icon">📈</span>
            Rapoarte
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => isActive ? "sidebar-link active" : "sidebar-link"}>
            <span className="icon">⚙️</span>
            Setări
          </NavLink>
          <button onClick={handleLogout} className="sidebar-link logout">
            <span className="icon">🚪</span>
            Deconectare
          </button>
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className="main-content">
        {/* Top Bar */}
        <div className="top-bar">
          <div className="welcome-section">
            <h2>Hello, <span className="welcome-name">Bine ai venit {user?.name || 'User'}!</span></h2>
            <div className="breadcrumb">Home - Dashboard</div>
          </div>
          <div className="user-section">
            <span className="user-email">{user?.email}</span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card stat-green">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <div className="stat-number">1,247+</div>
              <div className="stat-label">TOTAL PACIENȚI</div>
            </div>
          </div>
          <div className="stat-card stat-purple">
            <div className="stat-icon">⭐</div>
            <div className="stat-info">
              <div className="stat-number">15 Ani</div>
              <div className="stat-label">EXPERIENȚĂ MEDICALĂ</div>
            </div>
          </div>
          <div className="stat-card stat-blue">
            <div className="stat-icon">⏰</div>
            <div className="stat-info">
              <div className="stat-number">28</div>
              <div className="stat-label">PROGRAMĂRI ASTĂZI</div>
            </div>
          </div>
          <div className="stat-card stat-orange">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-number">€45,230</div>
              <div className="stat-label">VENIT LUNAR</div>
            </div>
          </div>
        </div>

        {/* Applications Section */}
        <div className="applications-section">
          <h3 className="section-title">Sisteme și Aplicații</h3>
          <div className="app-grid">
            <a href="https://sps.evoomedical.ro/login-traditional" className="app-item app-pink">
              <div className="app-icon">📅</div>
              <div className="app-details">
                <div className="app-name">SPS</div>
                <div className="app-access">Acces</div>
              </div>
            </a>
            <a href="/patients" className="app-item app-blue">
              <div className="app-icon">👥</div>
              <div className="app-details">
                <div className="app-name">Gestiune Pacienți</div>
                <div className="app-access">Acces</div>
              </div>
            </a>
            <a href="/doctors" className="app-item app-green">
              <div className="app-icon">👨‍⚕️</div>
              <div className="app-details">
                <div className="app-name">Portal Doctori</div>
                <div className="app-access">Acces</div>
              </div>
            </a>
            <a href="/reports" className="app-item app-orange">
              <div className="app-icon">📊</div>
              <div className="app-details">
                <div className="app-name">Rapoarte Medicale</div>
                <div className="app-access">Acces</div>
              </div>
            </a>
          </div>
        </div>

        {/* External Applications Section */}
        <div className="applications-section">
          <h3 className="section-title">Aplicații Externe</h3>
          <div className="app-grid">
            <a href="https://example.com/app1" className="app-item app-teal">
              <div className="app-icon">🔗</div>
              <div className="app-details">
                <div className="app-name">Aplicație Externă 1</div>
                <div className="app-access">Acces</div>
              </div>
            </a>
            <a href="https://example.com/app2" className="app-item app-purple">
              <div className="app-icon">💻</div>
              <div className="app-details">
                <div className="app-name">Aplicație Externă 2</div>
                <div className="app-access">Acces</div>
              </div>
            </a>
            <a href="https://example.com/app3" className="app-item app-red">
              <div className="app-icon">🌐</div>
              <div className="app-details">
                <div className="app-name">Aplicație Externă 3</div>
                <div className="app-access">Acces</div>
              </div>
            </a>
            <a href="https://example.com/app4" className="app-item app-yellow">
              <div className="app-icon">📱</div>
              <div className="app-details">
                <div className="app-name">Aplicație Externă 4</div>
                <div className="app-access">Acces</div>
              </div>
            </a>
          </div>
        </div>
      </main>

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .dashboard-layout {
          display: flex;
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
        }

        /* Sidebar Styles */
        .sidebar {
          width: 280px;
          background: white;
          box-shadow: 2px 0 10px rgba(0,0,0,0.1);
          display: flex;
          flex-direction: column;
          position: fixed;
          height: 100vh;
          left: 0;
          top: 0;
          z-index: 100;
        }

        .sidebar-header {
          padding: 2rem 1.5rem;
          border-bottom: 1px solid #e0e0e0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .sidebar-header h1 {
          font-size: 1.5rem;
          margin-bottom: 0.25rem;
        }

        .sidebar-header p {
          font-size: 0.9rem;
          opacity: 0.9;
        }

        .sidebar-nav {
          flex: 1;
          padding: 1rem 0;
          overflow-y: auto;
        }

        .sidebar-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.5rem;
          color: #666;
          text-decoration: none;
          transition: all 0.3s;
          border-left: 3px solid transparent;
          background: none;
          border-top: none;
          border-right: none;
          border-bottom: none;
          width: 100%;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
        }

        .sidebar-link .icon {
          font-size: 1.2rem;
        }

        .sidebar-link:hover {
          background: #f5f5f5;
          color: #667eea;
        }

        .sidebar-link.active {
          background: #f0f4ff;
          color: #667eea;
          border-left-color: #667eea;
          font-weight: 600;
        }

        .sidebar-link.logout {
          margin-top: auto;
          color: #dc3545;
        }

        .sidebar-link.logout:hover {
          background: #fff5f5;
        }

        /* Main Content Area */
        .main-content {
          flex: 1;
          margin-left: 280px;
          padding: 2rem;
          overflow-y: auto;
        }

        /* Top Bar */
        .top-bar {
          background: white;
          padding: 1.5rem 2rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .welcome-section h2 {
          font-size: 1.5rem;
          color: #333;
          margin-bottom: 0.5rem;
        }

        .welcome-name {
          color: #667eea;
        }

        .breadcrumb {
          font-size: 0.9rem;
          color: #999;
        }

        .user-email {
          font-size: 0.9rem;
          color: #666;
        }

        /* Stats Cards */
        .stats-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          transition: transform 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-5px);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: white;
        }

        .stat-green .stat-icon {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
        }

        .stat-purple .stat-icon {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .stat-blue .stat-icon {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
        }

        .stat-orange .stat-icon {
          background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
        }

        .stat-info {
          flex: 1;
        }

        .stat-number {
          font-size: 1.8rem;
          font-weight: bold;
          color: #333;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }

        /* Applications Section */
        .applications-section {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .section-title {
          font-size: 1.3rem;
          color: #333;
          margin-bottom: 1.5rem;
          font-weight: 600;
        }

        .app-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .app-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
          border: 2px solid #f0f0f0;
        }

        .app-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .app-icon {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          flex-shrink: 0;
        }

        .app-pink .app-icon {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        }

        .app-blue .app-icon {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
        }

        .app-green .app-icon {
          background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
        }

        .app-orange .app-icon {
          background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
        }

        .app-teal .app-icon {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
        }

        .app-purple .app-icon {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .app-red .app-icon {
          background: linear-gradient(135deg, #f857a6 0%, #ff5858 100%);
        }

        .app-yellow .app-icon {
          background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
        }

        .app-details {
          flex: 1;
        }

        .app-name {
          font-size: 1rem;
          font-weight: 600;
          color: #333;
          margin-bottom: 0.25rem;
        }

        .app-access {
          font-size: 0.85rem;
          color: #999;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .sidebar {
            width: 100%;
            height: auto;
            position: relative;
          }

          .main-content {
            margin-left: 0;
            padding: 1rem;
          }

          .stats-cards {
            grid-template-columns: 1fr;
          }

          .app-grid {
            grid-template-columns: 1fr;
          }

          .top-bar {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

// App Content Component (inside provider context)
function AppContent() {
  // Enable inactivity logout for authenticated users
  useInactivityLogout();

  return (
    <Router>
      <Routes>
        <Route path="/" element={<LoginEntra />} />
        <Route path="/login-entra" element={<LoginEntra />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Intranet />
          </ProtectedRoute>
        } />
        <Route path="/patients" element={
          <ProtectedRoute>
            <Patients />
          </ProtectedRoute>
        } />
        <Route path="/appointments" element={
          <ProtectedRoute>
            <Appointments />
          </ProtectedRoute>
        } />
        <Route path="/doctors" element={
          <ProtectedRoute>
            <Doctors />
          </ProtectedRoute>
        } />
        <Route path="/staff" element={
          <ProtectedRoute>
            <Staff />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/sisteme" element={
          <ProtectedRoute>
            <Sisteme />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

// Main App Component
function App() {
  return (
    <EntraAuthProvider>
      <EntraConfigChecker />
      <AppContent />
    </EntraAuthProvider>
  );
}

export default App;
